import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import EnhancedSessionManager from '../../utils/EnhancedSessionManager';

interface GoogleLoginButtonProps {
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  disabled?: boolean;
}

declare global {
  interface Window {
    google: any;
    googleSignInCallback?: (response: any) => void;
  }
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  disabled = false
}) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Google Client ID - using the correct one for the project
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const MAX_RETRY_ATTEMPTS = 3;

  useEffect(() => {
    // Validate Google Client ID is configured
    if (!GOOGLE_CLIENT_ID) {
      console.error('❌ REACT_APP_GOOGLE_CLIENT_ID environment variable is not set');
      setError('Google OAuth is not properly configured');
      return;
    }

    // Load Google Sign-In script only once
    const loadGoogleScript = () => {
      if (window.google) {
        setGoogleLoaded(true);
        return;
      }

      // Check if script is already loading
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => setGoogleLoaded(true));
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ Google Sign-In script loaded');
        setGoogleLoaded(true);
      };
      script.onerror = () => {
        console.error('❌ Failed to load Google Sign-In script');
        setError('Failed to load Google Sign-In');
      };
      document.head.appendChild(script);
    };

    loadGoogleScript();
  }, []); // Empty dependency array to prevent re-loading

  useEffect(() => {
    // Initialize Google Sign-In when script is loaded
    const initializeGoogleSignIn = () => {
      if (!window.google?.accounts?.id) {
        console.error('❌ Google Sign-In not available');
        setError('Google Sign-In not available');
        return;
      }

      try {
        // Initialize Google Sign-In with COOP-compatible settings
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true, // Use FedCM to avoid COOP issues
          itp_support: true,
          ux_mode: 'popup', // Use popup mode
          context: 'signin'
        });

        // Render the Google Sign-In button
        if (googleButtonRef.current) {
          window.google.accounts.id.renderButton(
            googleButtonRef.current,
            {
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              shape: 'rectangular',
              logo_alignment: 'left',
              width: 280
            }
          );
          console.log('✅ Google Sign-In button rendered');
        }
      } catch (error) {
        console.error('❌ Error initializing Google Sign-In:', error);
        setError('Failed to initialize Google Sign-In');
      }
    };

    if (googleLoaded) {
      initializeGoogleSignIn();
    }
  }, [googleLoaded]);

  const handleCredentialResponse = async (response: any, attemptNumber: number = 0) => {
    if (!response.credential) {
      console.error('❌ No credential in Google response');
      onError?.({ error: 'No credential received from Google' });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`🔐 Google OAuth credential received, sending to backend... (Attempt ${attemptNumber + 1}/${MAX_RETRY_ATTEMPTS + 1})`);

      // Send the credential to our backend with proper headers
      const authResponse = await fetch(
        'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/auth/google',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
          },
          credentials: 'omit', // Cross-origin API call, auth is via JWT in body not cookies
          body: JSON.stringify({ 
            idToken: response.credential 
          }),
        }
      );

      if (authResponse.ok) {
        const responseJson = await authResponse.json();
        
        // Backend wraps response in { success: true, data: {...} }
        // Unwrap the data if needed
        const authData = responseJson.success && responseJson.data ? responseJson.data : responseJson;
        
        console.log('✅ Enhanced authentication successful', {
          hasToken: !!authData.token,
          hasAccessToken: !!authData.accessToken,
          hasRefreshToken: !!authData.refreshToken,
          hasUser: !!authData.user,
          hasSession: !!authData.session,
          isNewUser: authData.isNewUser,
          responseStructure: responseJson.success ? 'wrapped' : 'direct'
        });
        
        // Use the enhanced auth response structure
        const accessToken = authData.token || authData.accessToken;
        
        if (accessToken && authData.user) {
          // Use EnhancedSessionManager to store session data properly
          const sessionId = authData.session?.sessionId || `session-${Date.now()}`;
          const expiresIn = authData.session?.expiresIn || 3600;
          const refreshToken = authData.refreshToken || accessToken;
          
          await EnhancedSessionManager.createSession(
            accessToken,
            refreshToken,
            authData.user,
            sessionId,
            expiresIn,
            {
              tokenType: 'Bearer',
              timestamp: new Date().toISOString(),
              isNewUser: authData.isNewUser || false,
              deviceInfo: authData.session?.deviceInfo
            }
          );
          
          console.log('✅ Enhanced auth data stored successfully using EnhancedSessionManager');
          console.log('🎉 AUTHENTICATION COMPLETED SUCCESSFULLY!');
          console.log('✅ You should now be logged in');
          
          onSuccess?.(authData);
        } else {
          throw new Error('Invalid authentication response: missing token or user data');
        }
      } else {
        const errorData = await authResponse.json();
        const errorMessage = errorData.error?.message || `Authentication failed (${authResponse.status})`;
        console.error('❌ Authentication failed:', errorMessage);
        
        // Check if this is a retryable error and we haven't exceeded max attempts
        const isRetryableError = authResponse.status >= 500 || authResponse.status === 429;
        if (isRetryableError && attemptNumber < MAX_RETRY_ATTEMPTS) {
          console.log(`🔄 Retryable error detected, attempting retry ${attemptNumber + 1}/${MAX_RETRY_ATTEMPTS}...`);
          setRetryCount(attemptNumber + 1);
          
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, attemptNumber) * 1000; // 1s, 2s, 4s
          setTimeout(() => {
            handleCredentialResponse(response, attemptNumber + 1);
          }, delay);
          return;
        }
        
        setError(errorMessage);
        onError?.({ error: errorMessage });
      }
    } catch (error: any) {
      console.error('❌ Authentication error:', error);
      const errorMessage = error.message || 'Network error during authentication';
      
      // Check if this is a network error and we haven't exceeded max attempts
      const isNetworkError = error.name === 'TypeError' || error.message.includes('fetch');
      if (isNetworkError && attemptNumber < MAX_RETRY_ATTEMPTS) {
        console.log(`🔄 Network error detected, attempting retry ${attemptNumber + 1}/${MAX_RETRY_ATTEMPTS}...`);
        setRetryCount(attemptNumber + 1);
        
        // Wait before retrying (exponential backoff)
        const delay = Math.pow(2, attemptNumber) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          handleCredentialResponse(response, attemptNumber + 1);
        }, delay);
        return;
      }
      
      setError(errorMessage);
      onError?.(error);
    } finally {
      setLoading(false);
      setRetryCount(0); // Reset retry count on completion
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    // Re-initialize Google Sign-In
    if (googleLoaded && window.google?.accounts?.id) {
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
          itp_support: true,
          ux_mode: 'popup',
          context: 'signin'
        });
      } catch (error) {
        console.error('❌ Error re-initializing Google Sign-In:', error);
      }
    }
  };

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: 280 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <button
              onClick={handleRetry}
              style={{
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem'
              }}
            >
              Retry
            </button>
          }
        >
          {error}
          {retryCount > 0 && (
            <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5 }}>
              Attempt {retryCount}/{MAX_RETRY_ATTEMPTS + 1}
            </Box>
          )}
        </Alert>
        <Box
          ref={googleButtonRef}
          sx={{
            display: googleLoaded ? 'block' : 'none',
            opacity: disabled ? 0.5 : 1,
            pointerEvents: disabled ? 'none' : 'auto'
          }}
        />
        {!googleLoaded && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 280 }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
            borderRadius: 1
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      
      <Box
        ref={googleButtonRef}
        sx={{
          display: googleLoaded ? 'block' : 'none',
          opacity: disabled || loading ? 0.5 : 1,
          pointerEvents: disabled || loading ? 'none' : 'auto'
        }}
      />
      
      {!googleLoaded && !loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};

export default GoogleLoginButton;